import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp, TrendingDown, Minus, CalendarOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RADAR_COLORS = ['#09D6B4', '#F59E0B', '#6366F1'];
const BADGE = { gold: '🥇', silver: '🥈', bronze: '🥉' };
const BADGE_BG = { gold: 'bg-amber-50 border-amber-200', silver: 'bg-slate-50 border-slate-300', bronze: 'bg-orange-50 border-orange-200' };

const LeaveTodayBanner = ({ domainId }) => {
  const [data, setData] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  useEffect(() => {
    const qs = domainId ? `?domain_id=${domainId}` : '';
    axios.get(`${API}/analytics/team-leave-today${qs}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
    axios.get(`${API}/analytics/team-leave-upcoming${qs}`)
      .then((r) => setUpcoming(r.data)).catch(() => setUpcoming(null));
  }, [domainId]);
  if (!data?.users?.length && !upcoming?.days?.length) return null;
  const leave = (data?.users || []).filter((u) => u.reason === 'leave');
  const dayOff = (data?.users || []).filter((u) => u.reason === 'day_off');
  const names = (arr) => arr.map((u) => u.name).join(', ');
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 flex items-start gap-2" data-testid="team-leave-banner">
      <CalendarOff className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        {(data?.users || []).length > 0 ? (
          <>
            <span className="font-bold">Off today ({data.day}):</span>{' '}
            {leave.length > 0 && <span data-testid="team-leave-names">on leave — {names(leave)}</span>}
            {leave.length > 0 && dayOff.length > 0 && ' · '}
            {dayOff.length > 0 && <span data-testid="team-dayoff-names">not a work day — {names(dayOff)}</span>}
          </>
        ) : (
          <span className="font-bold">Everyone is in today.</span>
        )}
        {upcoming?.days?.length > 0 && (
          <span className="block text-xs mt-0.5" data-testid="team-leave-upcoming">
            <strong>Coming up:</strong>{' '}
            {upcoming.days.slice(0, 4).map((d) => `${d.day} — ${d.users.map((u) => u.name).join(', ')}`).join(' · ')}
          </span>
        )}
        <span className="block text-xs opacity-70 mt-0.5">Off days come from each user's "Work days &amp; leave" settings.</span>
      </div>
    </div>
  );
};

const TeamTab = ({ domainId = null, onAgentClick = null, onSwitchTab = null }) => {
  const { userIndustry } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    else if (userIndustry) p.set('call_type', userIndustry);
    const qs = p.toString() ? `?${p}` : '';
    axios.get(`${API}/analytics/team${qs}`, { timeout: 35000 })
      .then(r => setData(r.data))
      .catch(() => setData({ leaderboard: [], radar_data: [], top_agents: [] }))
      .finally(() => setLoading(false));
  }, [userIndustry, domainId]);

  if (loading) return <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>;
  if (!data?.leaderboard?.length) return (
    <div className="py-16 text-center text-slate-500" data-testid="team-empty-state">
      <p className="text-base font-medium text-slate-600">No team data yet</p>
      <p className="text-sm text-slate-400 mt-1">Team leaderboard appears once calls are graded.</p>
    </div>
  );

  const { leaderboard, radar_data, top_agents } = data;
  const maxScore = Math.max(...leaderboard.map(a => a.avg_score));

  const goToAgentCalls = (agentId) => {
    if (onAgentClick) { onAgentClick(agentId); return; }
    if (onSwitchTab) { onSwitchTab('calls', null, agentId); return; }
    navigate(`/calls?agent_id=${agentId}`);
  };

  return (
    <div className="space-y-6" data-testid="team-tab">
      <LeaveTodayBanner domainId={domainId} />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Leaderboard */}
        <Card className="border-slate-200 shadow-sm lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base">Agent Leaderboard <span className="text-xs font-normal text-slate-400 ml-1">— click agent to view their calls</span></CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {leaderboard.map((agent) => (
              <div
                key={agent.agent_id}
                onClick={() => goToAgentCalls(agent.agent_id)}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer hover:shadow-md ${agent.badge ? BADGE_BG[agent.badge] : 'bg-white border-slate-100'} hover:border-teal-300`}
                data-testid={`leaderboard-${agent.agent_id}`}
                title={`View ${agent.agent_name}'s calls`}
              >
                <span className="text-xl w-8 text-center">
                  {agent.badge ? BADGE[agent.badge] : <span className="text-sm font-bold text-slate-400">#{agent.rank}</span>}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-slate-900 text-sm truncate hover:text-teal-600 transition-colors">{agent.agent_name}</span>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      {agent.trend > 0.5 ? <TrendingUp className="w-4 h-4 text-emerald-500" /> :
                       agent.trend < -0.5 ? <TrendingDown className="w-4 h-4 text-rose-500" /> :
                       <Minus className="w-4 h-4 text-slate-400" />}
                      <span className="text-base font-bold text-slate-900">{agent.avg_score}</span>
                    </div>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full bg-primary transition-all"
                      style={{ width: `${(agent.avg_score / maxScore) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{agent.total_calls} calls · click to view</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Radar Chart */}
        <Card className="border-slate-200 shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Top 3 Skills Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            {radar_data?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radar_data} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="criteria" tick={{ fontSize: 9 }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  {top_agents.map((name, idx) => (
                    <Radar
                      key={name}
                      name={name}
                      dataKey={name}
                      stroke={RADAR_COLORS[idx]}
                      fill={RADAR_COLORS[idx]}
                      fillOpacity={0.15}
                      strokeWidth={2}
                    />
                  ))}
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Tooltip formatter={(v) => `${v}%`} />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-sm text-center py-8">Not enough data for comparison</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TeamTab;
